import { useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Star } from "lucide-react";
import { SITE, FACTORIES } from "../data/site";
import { useLang } from "../i18n";
import { MaskedLine, Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { WhatsAppIcon } from "../components/FloatingContact";

export default function Contact() {
  const { t } = useLang();

  useEffect(() => {
    document.title = "Contact Us | Sana Steel Industries, Bengaluru";
  }, []);

  const ROWS = [
    {
      icon: Phone,
      label: t("row.call"),
      content: (
        <>
          <a href={SITE.phone1Href} data-testid="contact-phone-1" className="block text-ink hover:text-steel transition-colors duration-200">{SITE.phone1}</a>
          <a href={SITE.phone2Href} data-testid="contact-phone-2" className="block text-ink hover:text-steel transition-colors duration-200">{SITE.phone2}</a>
        </>
      ),
    },
    {
      icon: Mail,
      label: t("row.email"),
      content: (
        <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="text-ink hover:text-steel transition-colors duration-200 break-all">
          {SITE.email}
        </a>
      ),
    },
    {
      icon: MapPin,
      label: t("row.address"),
      content: (
        <div className="space-y-3">
          {FACTORIES.map((f) => (
            <div key={f.key} data-testid={`address-${f.key}`}>
              <p className="font-heading text-xs font-bold uppercase tracking-wide text-navy">{f.name}</p>
              <p className="text-ink text-sm mt-0.5 leading-relaxed">{f.address}</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: Clock,
      label: t("row.hours"),
      content: <span className="text-ink">{SITE.hours}</span>,
    },
  ];

  return (
    <main data-testid="contact-page" className="pt-20">
      <section className="bg-navy py-24 md:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <MaskedLine delay={0.1}>
            <span className="section-eyebrow-dark inline-flex items-center gap-3">
              <span className="w-8 h-[2px] bg-silver inline-block" />
              {t("contact.eyebrow")}
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-white leading-[0.95] text-5xl md:text-7xl">
            <MaskedLine delay={0.25}>{t("contact.l1")}</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-silver">{t("contact.l2")}</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="contact-details-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading text-3xl font-black uppercase tracking-tight text-navy">
                {t("contact.reach")}
              </h2>
              <div className="mt-10 divide-y divide-gray-200 border-t border-b border-gray-200">
                {ROWS.map((r) => (
                  <div key={r.label} className="py-6 flex items-start gap-5" data-testid={`contact-row-${r.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="w-10 h-10 bg-surface border border-gray-200 flex items-center justify-center shrink-0">
                      <r.icon size={18} className="text-navy" />
                    </span>
                    <div>
                      <p className="field-label !mb-1">{r.label}</p>
                      <div className="text-base">{r.content}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={SITE.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-primary"
                   className="inline-flex items-center gap-2 bg-[#25D366] text-white font-heading font-bold uppercase tracking-wide text-xs px-6 py-3 transition-colors duration-200 hover:bg-navy">
                  <WhatsAppIcon size={16} /> WhatsApp {SITE.phone2}
                </a>
                <a href={SITE.whatsappSecondary} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-secondary"
                   className="inline-flex items-center gap-2 border border-gray-300 text-navy font-heading font-bold uppercase tracking-wide text-xs px-6 py-3 transition-colors duration-200 hover:border-[#25D366] hover:text-[#25D366]">
                  <WhatsAppIcon size={16} /> {SITE.phone1}
                </a>
                <a href={SITE.googleReview} target="_blank" rel="noreferrer" data-testid="contact-google-review"
                   className="inline-flex items-center gap-2 border border-gray-300 text-navy font-heading font-bold uppercase tracking-wide text-xs px-6 py-3 transition-colors duration-200 hover:border-navy hover:bg-surface">
                  <Star size={16} /> {t("review.google")}
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FACTORIES.map((f) => (
                <div key={f.key} data-testid={`map-${f.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-navy">{f.name}</p>
                    <a href={f.directions} target="_blank" rel="noreferrer" data-testid={`directions-${f.key}`}
                       className="text-xs font-semibold text-steel hover:text-navy transition-colors duration-200">
                      Directions →
                    </a>
                  </div>
                  <iframe
                    title={`Sana Steel Industries — ${f.name}`}
                    src={f.embed}
                    className="w-full h-[280px] md:h-[300px] grayscale contrast-110 border border-gray-200"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-surface" data-testid="contact-form-section">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Reveal className="mb-12">
            <p className="section-eyebrow">{t("contact.form.eyebrow")}</p>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-black uppercase tracking-tight text-navy">
              {t("contact.form.h2")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <QuoteForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
